import { TFunction } from "react-i18next";
import { MoneyApi } from "go-core/api/types";
import FormatMoney from "go-core/components/Formatters/FormatMoney";
import { RenderColumnInArea, RenderColumnInAreaType } from "go-report/core/services/types";
import ReportOrdersUtils from "./ReportOrdersUtils";

export class ReportGuestsUtils {
	static getSelectedGroups() {
		return ["extended.number_of_guests", "extended.average_per_guest_money", "total_money"];
	}

	static getChartParseGroup(group: string) {
		if (group === "HOUR_OF_DAY") return "CLOSED_AT_HOUR_OF_DAY";
		if (group === "DAY_OF_MONTH") return "CLOSED_AT_DAY_OF_MONTH";
		if (group === "DATE") return "CLOSED_AT_DATE";
		if (group === "DAY_OF_WEEK") return "CLOSED_AT_DAY_OF_WEEK";
		if (group === "MONTH") return "CLOSED_AT_MONTH";
		return group;
	}

	static getColumns(t: TFunction) {
		return [
			...ReportOrdersUtils.getColumns(t),
			{
				id: "extended.number_of_guests",
				name: t("modules.report_guest.field.number_of_guests.title"),
				render: (item: number) => item,
			},
			{
				id: "extended.average_per_guest_money",
				name: t("modules.report_guest.field.average_per_guest_money.title"),
				renderView: (
					item: MoneyApi,
					fullAggregateObject: Record<string, any>,
					{ rowType, columnType }: RenderColumnInArea,
					renderPDF?: boolean
				) => {
					const renderingSummaryAggregateForGrouping =
						rowType === RenderColumnInAreaType.GROUP && columnType === RenderColumnInAreaType.SUMMARY;

					if (renderPDF && !renderingSummaryAggregateForGrouping) {
						return item.amount;
					}

					if (renderingSummaryAggregateForGrouping) {
						const totalGuestsMoney = fullAggregateObject.extended.total_guests_money?.amount;
						const {
							extended: { number_of_guests },
						} = fullAggregateObject;

						if (!number_of_guests || !totalGuestsMoney) return renderPDF ? 0 : FormatMoney(item);

						const averagePerGuestMoney = totalGuestsMoney / number_of_guests;

						return renderPDF
							? averagePerGuestMoney.toFixed(2)
							: FormatMoney({ ...item, amount: averagePerGuestMoney });
					}

					return FormatMoney(item);
				},
				renderCsv: (
					item: MoneyApi,
					fullAggregateObject: Record<string, any>,
					{ rowType, columnType }: RenderColumnInArea
				) => {
					if (rowType === RenderColumnInAreaType.GROUP && columnType === RenderColumnInAreaType.SUMMARY) {
						const totalGuestsMoney = fullAggregateObject.extended.total_guests_money?.amount;
						const {
							extended: { number_of_guests },
						} = fullAggregateObject;

						if (!number_of_guests || !totalGuestsMoney) return 0;

						const averagePerGuestMoney = totalGuestsMoney / number_of_guests;

						return averagePerGuestMoney.toFixed(2);
					}

					return item.amount;
				},
			},
			{
				id: "extended.net_average_per_guest_money",
				name: t("modules.report_guest.field.net_average_per_guest_money.title"),
				renderView: (
					item: MoneyApi,
					fullAggregateObject: Record<string, any>,
					{ rowType, columnType }: RenderColumnInArea,
					renderPDF?: boolean
				) => {
					const renderingSummaryAggregateForGrouping =
						rowType === RenderColumnInAreaType.GROUP && columnType === RenderColumnInAreaType.SUMMARY;

					if (renderPDF && !renderingSummaryAggregateForGrouping) {
						return item.amount;
					}

					if (renderingSummaryAggregateForGrouping) {
						const totalNetGuestsMoney = fullAggregateObject.extended.total_net_guests_money?.amount;
						const {
							extended: { number_of_guests },
						} = fullAggregateObject;

						if (!number_of_guests || !totalNetGuestsMoney) return renderPDF ? 0 : FormatMoney(item);

						const averageNetPerGuestMoney = totalNetGuestsMoney / number_of_guests;

						return renderPDF
							? averageNetPerGuestMoney.toFixed(2)
							: FormatMoney({ ...item, amount: averageNetPerGuestMoney });
					}

					return FormatMoney(item);
				},
				renderCsv: (
					item: MoneyApi,
					fullAggregateObject: Record<string, any>,
					{ rowType, columnType }: RenderColumnInArea
				) => {
					if (rowType === RenderColumnInAreaType.GROUP && columnType === RenderColumnInAreaType.SUMMARY) {
						const totalNetGuestsMoney = fullAggregateObject.extended.total_net_guests_money?.amount;
						const {
							extended: { number_of_guests },
						} = fullAggregateObject;

						if (!number_of_guests || !totalNetGuestsMoney) return 0;

						const averageNetPerGuestMoney = totalNetGuestsMoney / number_of_guests;

						return averageNetPerGuestMoney.toFixed(2);
					}

					return item.amount;
				},
			},
		];
	}
}
